/** Assistant message engine — Phases 1–3 (no external LLM). */

import { getUserRoleBucket, roleCanManageUsers, roleCanWrite } from "assistant/userRole";
import {
  searchFaq,
  isFaqQuery,
  resolveRouteKey,
  getPageHelp,
  getRouteByKey,
  NAV_ALIASES,
} from "assistant/knowledge";
import {
  detectIntent,
  extractSearchTerm,
  extractTeacherStoryTerm,
  isHelpOnPageQuery,
  isCapabilitiesQuery,
  isTeacherFollowUpQuery,
  isTeacherSearchWithoutName,
  isOffTopicQuery,
} from "assistant/intents";
import { resolveTeacherSearchReply } from "assistant/teacherQuery";
import { isAwaitingTeacherSearchName, extractLastTeacherProfileFromMessages, isAwaitingLeaveDetails } from "assistant/conversationContext";
import { classifyRouteIntent, isActionableRoute, writeDraftFromRoute } from "assistant/intentRouter";
import { getAssistantCapabilities, getQuickPromptTiles } from "assistant/capabilities";
import {
  fetchTeacherStats,
  searchTeachers,
  fetchRecruiterCount,
  fetchLeaveStats,
  fetchAssignmentStats,
  fetchEstablishmentStats,
  fetchDashboardSummaryCards,
  fetchProactiveInsights,
  fetchAreaAttendanceSummary,
  fetchEstablishmentAttendanceReport,
  fetchLicenseQuota,
  fetchUnlicensedTeachers,
  findTeacherById,
} from "assistant/apiHandlers";
import { startWorkflow, nextWorkflowStep } from "assistant/workflows";
import { isLlmEnabled } from "assistant/llmClient";
import {
  buildRuleFacts,
  prefetchLiveContext,
  LLM_ENHANCE_INTENTS,
} from "assistant/hybridContext";
import { buildPageAwareContext, formatPageAwareForLlm } from "assistant/pageAware";
import { fetchTeacherStory } from "assistant/teacherStory";
import {
  shouldHandleWriteCommand,
  handleWriteCommandMessage,
  handleLeaveDetailsReply,
  continueWriteCommand,
  executeWriteCommand,
  cancelWriteCommandMessage,
  executeClassifiedWriteDraft,
} from "assistant/commandHandlers";

function packData(response, ruleFacts) {
  return { response, ruleFacts };
}

async function deliverHybrid(response, userMessage, context, ruleFacts) {
  if (!response) return null;
  if (
    !context.aiRoutingEnabled ||
    !isLlmEnabled() ||
    !ruleFacts ||
    !LLM_ENHANCE_INTENTS.has(ruleFacts.intent)
  ) {
    return response;
  }
  // Progressive UX: return rules/cards/stats immediately; PortalAssistant enhances narrative in background.
  return {
    ...response,
    llmPending: true,
    enhanceRequest: {
      userMessage: String(userMessage || "").slice(0, 2000),
      ruleFacts,
      intent: ruleFacts.intent,
    },
  };
}

async function deliverDataResult(dataResult, userMessage, context) {
  if (!dataResult) return null;
  if (dataResult.response) {
    return deliverHybrid(dataResult.response, userMessage, context, dataResult.ruleFacts);
  }
  return dataResult;
}

const WIZARD_INTENT_MAP = {
  wizard_affectation: "affectation",
  wizard_leave: "leave",
  wizard_teacher: "teacher",
  wizard_attendance: "attendance",
};

function msg(partial) {
  return {
    id: partial.id || `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    role: partial.role || "assistant",
    type: partial.type || "text",
    content: partial.content || "",
    actions: partial.actions || [],
    cards: partial.cards || [],
    wizard: partial.wizard || null,
    insights: partial.insights || [],
    suggestions: partial.suggestions || [],
    teacherResults: partial.teacherResults || [],
    attendanceResults: partial.attendanceResults || [],
    attendanceLabel: partial.attendanceLabel || "",
    storyTimeline: partial.storyTimeline || [],
    pageAware: partial.pageAware || null,
    timestamp: Date.now(),
    ...partial,
  };
}

function tReplace(template, vars) {
  let out = template;
  Object.entries(vars).forEach(([k, v]) => {
    out = out.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), String(v));
  });
  return out;
}

function defaultSuggestions(language, roleBucket) {
  const { promptTiles = [] } = getQuickPromptTiles(language, roleBucket);
  return promptTiles
    .filter((t) => t.id !== "cap-what" && t.id !== "cap-page-help")
    .slice(0, 4)
    .map((t) => t.message);
}

export function buildWelcomeMessage(context) {
  const { language, user, pathname, pageContext } = context;
  const roleBucket = getUserRoleBucket(user);
  const pageHelp = getPageHelp(pathname, language);
  const pageAware = buildPageAwareContext(pathname, pageContext, language);

  const content =
    language === "en"
      ? `Hello! I'm your **eVerifPro assistant**. I can look up live portal data, find teachers, guide you through tasks, and help with leave, assignments, and licenses — just ask in plain language.`
      : `Bonjour ! Je suis votre **assistant eVerifPro**. Données live, recherche d'enseignantes, guides pas à pas, congés, affectations et licences — posez vos questions naturellement.`;

  let hints = "";
  if (pageAware?.summary) {
    hints =
      language === "en"
        ? `**On this screen:**\n${pageAware.summary}`
        : `**Sur cet écran :**\n${pageAware.summary}`;
  } else if (pageHelp) {
    hints =
      language === "en"
        ? `You're on **${pageHelp.title}**. Ask "help on this page" for tips.`
        : `Vous êtes sur **${pageHelp.title}**. Demandez « aide sur cette page » pour des conseils.`;
  }

  return msg({
    type: "welcome",
    content: hints ? `${content}\n\n${hints}` : content,
    suggestions: [
      language === "en" ? "What can you do for me?" : "Que peux-tu faire pour moi ?",
      language === "en" ? "Take a quick tour" : "Visite guidée",
      language === "en" ? "Help on this page" : "Aide sur cette page",
      ...(pageAware?.suggestions?.length ? pageAware.suggestions.slice(0, 1) : defaultSuggestions(language, roleBucket).slice(0, 1)),
    ],
    pageAware,
  });
}

export async function loadProactiveInsights(context) {
  const { language, t } = context;
  try {
    const { insights } = await fetchProactiveInsights();
    return insights.map((ins) => ({
      ...ins,
      title: tReplace(t(ins.titleKey, ins.titleFallback), { count: ins.count }),
      actionLabel: t(ins.actionKey, ins.actionFallback),
    }));
  } catch {
    return [];
  }
}

async function buildTeacherStoryResponse(context, { teacherId, query, userMessage }) {
  const { language, pageContext, t } = context;
  const pageAware = buildPageAwareContext(context.pathname, pageContext, language);
  const pageTeacherId = pageAware?.selection?.teacherId || pageContext?.selection?.teacherId;
  const term = String(query ?? "").trim();
  const resolvedTeacherId = teacherId || (!term ? pageTeacherId : null);

  if (!term && !resolvedTeacherId) {
    return msg({
      content:
        language === "en"
          ? "Which teacher? Example: « Tell me about teacher Hamza » or « registration no 600700 »."
          : "Quelle enseignante ? Ex. « Fiche enseignant Hamza » ou « matricule 600700 ».",
      suggestions: pageAware?.suggestions?.length ? pageAware.suggestions : undefined,
    });
  }

  const result = await fetchTeacherStory(term, {
    teacherId: resolvedTeacherId,
    pageContext,
    language,
  });

  if (!result.ok && result.reason === "ambiguous") {
    return msg({
      type: "teacher_pick",
      content:
        language === "en"
          ? `Several teachers match « ${result.query} ». **Click a row** below to load the full story:`
          : `Plusieurs enseignantes correspondent à « ${result.query} ». **Cliquez sur une ligne** pour charger la fiche :`,
      teacherResults: result.matches,
      teacherResultsSelectable: true,
    });
  }

  if (!result.ok) {
    return msg({
      type: "error",
      content:
        language === "en"
          ? result.reason === "error"
            ? `Could not load teacher story. ${result.error || "Try again."}`
            : `No teacher found for « ${term || resolvedTeacherId} ». Try a matricule (e.g. 600700) or full name.`
          : result.reason === "error"
            ? `Impossible de charger la fiche. ${result.error || "Réessayez."}`
            : `Aucune enseignante trouvée pour « ${term || resolvedTeacherId} ». Essayez un matricule (ex. 600700) ou le nom complet.`,
    });
  }

  const { story, structuredText, timeline } = result;
  const summary = story.summary || {};
  const ranking = story.ranking || {};
  const assignment = story.currentAssignment;
  const intro =
    language === "en"
      ? `Profile card for **${story.teacher.name}** — photo, score, assignment, leave, and attendance in one place.`
      : `Fiche de **${story.teacher.name}** — photo, score, affectation, congé et présence en un seul endroit.`;

  const mapParams = new URLSearchParams();
  if (story.teacher.id) mapParams.set("teacherId", String(story.teacher.id));
  if (story.teacher.reg && story.teacher.reg !== "—") mapParams.set("regNumber", String(story.teacher.reg));
  if (assignment?.estabId) mapParams.set("estabId", String(assignment.estabId));
  if (assignment?.estabName) mapParams.set("estabName", String(assignment.estabName));

  const teacherProfile = {
    teacher: story.teacher,
    summary,
    ranking,
    assignment,
    leaveSummary: story.leaveSummary,
    recentAttendance: (story.recentAttendance || []).slice(0, 3),
    charts: story.charts
      ? { attendanceByMonth: (story.charts.attendanceByMonth || []).slice(-6) }
      : null,
  };

  return packData(
    msg({
      type: "story",
      content: intro,
      teacherProfile,
      storyTeacher: story.teacher,
      storyTimeline: timeline,
      actions: [
        {
          id: "nav-profile",
          label: language === "en" ? "Full profile" : "Fiche complète",
          route: `/enseignant/${story.teacher.id}`,
          variant: "primary",
        },
        {
          id: "nav-history",
          label: language === "en" ? "History" : "Historique",
          route: `/histoire-des-enseignants/${story.teacher.id}`,
          variant: "secondary",
        },
        {
          id: "nav-map",
          label: language === "en" ? "Map" : "Carte",
          route: `/carte-enseignants?${mapParams.toString()}`,
          variant: "secondary",
        },
      ],
      suggestions: [
        language === "en" ? "Search another teacher" : "Chercher une autre enseignante",
        language === "en" ? "Pending leave" : "Congés en attente",
        language === "en" ? "License quota" : "Quota licences",
      ],
    }),
    buildRuleFacts({
      intent: "teacher_story",
      structuredText,
      facts: {
        teacherStory: {
          teacher: {
            id: story.teacher.id,
            name: story.teacher.name,
            reg: story.teacher.reg,
            status: story.teacher.status,
            score: story.teacher.score,
            isLicensed: story.teacher.isLicensed,
          },
          summary,
          ranking,
          leaves: story.leaves.length,
          assignment: assignment?.estabName || null,
        },
      },
      pageAware,
    })
  );
}

async function handleDataIntent(intentId, input, context) {
  const { language, roleBucket, t, pageContext } = context;

  try {
    if (intentId === "count_teachers") {
      const stats = await fetchTeacherStats();
      const text =
        language === "en"
          ? `There are ${stats.totalTeachers} teachers in the directory.`
          : `Il y a ${stats.totalTeachers} enseignante(s) dans l'annuaire.`;
      return packData(
        msg({
          type: "stats",
          content: language === "en" ? `There are **${stats.totalTeachers}** teachers in the directory.` : `Il y a **${stats.totalTeachers}** enseignante(s) dans l'annuaire.`,
          cards: [{ label: language === "en" ? "Teachers" : "Enseignantes", value: stats.totalTeachers }],
          actions: [{ id: "nav-teachers", label: t("assistant.action.view_teachers", "Voir enseignantes"), route: "/enseignantes", variant: "primary" }],
        }),
        buildRuleFacts({ intent: "count_teachers", structuredText: text, facts: { teachers: stats } })
      );
    }

    if (intentId === "count_recruiters") {
      const { count } = await fetchRecruiterCount();
      const text = language === "en" ? `${count} recruiters registered` : `${count} recruteur(s) enregistré(s)`;
      return packData(
        msg({
          type: "stats",
          content: language === "en" ? `**${count}** recruiter(s) registered.` : `**${count}** recruteur(s) enregistré(s).`,
          cards: [{ label: language === "en" ? "Recruiters" : "Recruteurs", value: count }],
          actions: [{ id: "nav-rec", label: t("assistant.action.view_recruiters", "Voir recruteurs"), route: "/recruiters", variant: "primary" }],
        }),
        buildRuleFacts({ intent: "count_recruiters", structuredText: text, facts: { recruiters: { count } } })
      );
    }

    if (intentId === "count_leaves") {
      const { stats, pendingPreview } = await fetchLeaveStats();
      const lines =
        language === "en"
          ? `Leave requests: **${stats.total}** total, **${stats.pending}** pending, **${stats.approved}** approved.`
          : `Demandes de congé : **${stats.total}** au total, **${stats.pending}** en attente, **${stats.approved}** approuvée(s).`;

      const actions = [{ id: "nav-leave", label: t("assistant.action.review_leaves", "Voir les congés"), route: "/leave-requests", variant: "primary" }];
      if (roleCanWrite(roleBucket) && stats.pending > 0) {
        actions.push({ id: "wizard-leave", label: t("assistant.action.leave_guide", "Guide congé"), action: "wizard:leave" });
      }

      return packData(
        msg({
          type: "stats",
          content: lines,
          cards: [
            { label: language === "en" ? "Pending" : "En attente", value: stats.pending, highlight: stats.pending > 0 },
            { label: language === "en" ? "Approved" : "Approuvées", value: stats.approved },
          ],
          actions,
          insights: stats.pending > 0 ? [{ severity: "warning", title: lines }] : [],
        }),
        buildRuleFacts({
          intent: "count_leaves",
          structuredText: lines.replace(/\*\*/g, ""),
          facts: { leaves: stats },
        })
      );
    }

    if (intentId === "count_assignments") {
      const stats = await fetchAssignmentStats();
      const text =
        language === "en"
          ? `${stats.totalRecords} assignments (${stats.thisMonthTotal} this month)`
          : `${stats.totalRecords} affectations (${stats.thisMonthTotal} ce mois)`;
      return packData(
        msg({
          type: "stats",
          content:
            language === "en"
              ? `**${stats.totalRecords}** assignment(s) on record (${stats.thisMonthTotal} this month).`
              : `**${stats.totalRecords}** affectation(s) enregistrée(s) (${stats.thisMonthTotal} ce mois-ci).`,
          cards: [
            { label: language === "en" ? "Total" : "Total", value: stats.totalRecords },
            { label: language === "en" ? "This month" : "Ce mois", value: stats.thisMonthTotal },
          ],
          actions: [{ id: "nav-aff", label: t("assistant.action.view_assignments", "Voir affectations"), route: "/affectations", variant: "primary" }],
        }),
        buildRuleFacts({ intent: "count_assignments", structuredText: text, facts: { assignments: stats } })
      );
    }

    if (intentId === "count_establishments") {
      const stats = await fetchEstablishmentStats();
      const text =
        language === "en"
          ? `${stats.totalRecords} establishments`
          : `${stats.totalRecords} établissements`;
      return packData(
        msg({
          type: "stats",
          content:
            language === "en"
              ? `**${stats.totalRecords}** establishment(s) in the directory.`
              : `**${stats.totalRecords}** établissement(s) dans le répertoire.`,
          cards: [{ label: language === "en" ? "Establishments" : "Établissements", value: stats.totalRecords }],
          actions: [{ id: "nav-est", label: t("assistant.action.view_establishments", "Voir établissements"), route: "/establishments", variant: "primary" }],
        }),
        buildRuleFacts({ intent: "count_establishments", structuredText: text, facts: { establishments: stats } })
      );
    }

    if (intentId === "dashboard_summary") {
      const cards = await fetchDashboardSummaryCards(language);
      const attendance = await fetchAreaAttendanceSummary().catch(() => null);
      let extra = "";
      if (attendance?.teachersChecked != null) {
        extra =
          language === "en"
            ? ` Area attendance (last 30 days): ${attendance.teachersChecked}/${attendance.teachersAssigned} checked (${attendance.checkRate}%).`
            : ` Présence zone (30 derniers jours) : ${attendance.teachersChecked}/${attendance.teachersAssigned} vérifiées (${attendance.checkRate} %).`;
      }
      const structuredText = cards.map((c) => `${c.label}: ${c.value}`).join(", ") + extra;
      return packData(
        msg({
          type: "stats",
          content:
            (language === "en"
              ? "Here's a live snapshot of your portal:"
              : "Voici un aperçu en direct de votre portail :") +
            (attendance?.teachersChecked != null
              ? language === "en"
                ? `\n\nArea attendance (last 30 days): **${attendance.teachersChecked}/${attendance.teachersAssigned}** checked (**${attendance.checkRate}%**).`
                : `\n\nPrésence zone (30 j) : **${attendance.teachersChecked}/${attendance.teachersAssigned}** vérifiées (**${attendance.checkRate} %**).`
              : ""),
          cards,
          actions: [
            { id: "nav-dash", label: t("assistant.action.view_dashboard", "Tableau de bord"), route: "/dashboard", variant: "primary" },
            { id: "nav-stats", label: t("assistant.action.view_stats", "Statistiques"), route: "/statistiques" },
            { id: "nav-area-att", label: language === "en" ? "Area attendance" : "Synthèse zone", route: "/synthese-presence-zone" },
          ],
        }),
        buildRuleFacts({ intent: "dashboard_summary", structuredText, facts: { dashboard: cards, areaAttendance: attendance } })
      );
    }

    if (intentId === "teacher_story") {
      const pageAware = buildPageAwareContext(context.pathname, pageContext, language);
      const pageTeacherId = pageAware?.selection?.teacherId || pageContext?.selection?.teacherId;
      const term = extractTeacherStoryTerm(input, language);

      if (!term && !pageTeacherId) {
        return msg({
          content:
            language === "en"
              ? "Which teacher? Example: « Is teacher Hamza assigned and on leave? » or « registration no 600700 »."
              : "Quelle enseignante ? Ex. « Est-ce que Hamza est affectée ? » ou « matricule 600700 ».",
          suggestions: pageAware?.suggestions?.length ? pageAware.suggestions : undefined,
        });
      }

      return buildTeacherStoryResponse(context, {
        teacherId: !term ? pageTeacherId : null,
        query: term,
        userMessage: input,
      });
    }

    if (intentId === "search_teacher") {
      if (isTeacherSearchWithoutName(input)) {
        return msg({
          type: "search",
          content:
            language === "en"
              ? "I can look up any teacher by **name** or **matricule** (registration number). Just reply with the name or number — for example a surname or matricule."
              : "Je peux retrouver une enseignante par **nom** ou **matricule**. Répondez simplement avec le nom ou le numéro.",
          suggestions: [
            language === "en" ? "Dupont" : "Dupont",
            language === "en" ? "Portal summary" : "Résumé du portail",
          ],
          actions: [
            {
              id: "nav-teachers",
              label: t("assistant.action.view_teachers", "Voir enseignantes"),
              route: "/enseignantes",
              variant: "primary",
            },
          ],
        });
      }

      const term = extractSearchTerm(input, language);
      if (!term || term.length < 2) {
        return msg({
          content:
            language === "en"
              ? "Please provide a name or registration number to search (e.g. « search teacher: Dupont »)."
              : "Indiquez un nom ou matricule (ex. « chercher enseignant: Dupont »).",
          suggestions: [
            language === "en" ? "Tell me about teacher registration no 600700" : "Fiche enseignant matricule 600700",
          ],
        });
      }
      const { items, totalMatches } = await searchTeachers(term, 6);

      // Unique match → open rich profile card immediately
      if (items.length === 1 && items[0]?.id) {
        return buildTeacherStoryResponse(context, {
          teacherId: items[0].id,
          query: term,
          userMessage: input,
        });
      }

      if (!items.length) {
        return msg({
          content:
            language === "en"
              ? `No teachers found for « ${term} ». Try a matricule or a fuller name.`
              : `Aucune enseignante trouvée pour « ${term} ». Essayez un matricule ou le nom complet.`,
          suggestions: [
            language === "en" ? "How many teachers?" : "Combien d'enseignantes ?",
            language === "en" ? "Portal summary" : "Résumé du portail",
          ],
        });
      }

      const matchCount = totalMatches ?? items.length;
      const teacherLines = items.map((row) => `${row.name} (${row.reg})`).join("; ");
      return packData(
        msg({
          type: "search",
          content:
            language === "en"
              ? `Found **${matchCount}** match(es) for « ${term} ». Click a card to open the full profile:`
              : `**${matchCount}** résultat(s) pour « ${term} ». Cliquez une carte pour ouvrir la fiche :`,
          teacherResults: items,
          teacherResultsSelectable: true,
          actions: [
            {
              id: "nav-teachers",
              label: t("assistant.action.view_teachers", "Voir enseignantes"),
              route: "/enseignantes",
              variant: "secondary",
            },
          ],
          suggestions: [
            language === "en" ? "Tell me about teacher registration no 600700" : "Fiche enseignant matricule 600700",
          ],
        }),
        buildRuleFacts({
          intent: "search_teacher",
          structuredText:
            language === "en"
              ? `Search "${term}": ${matchCount} match(es). Top results: ${teacherLines}.`
              : `Recherche « ${term} » : ${matchCount} résultat(s). Résultats : ${teacherLines}.`,
          facts: {
            search: {
              term,
              count: matchCount,
              items: items.map((row) => ({
                id: row.id,
                name: row.name,
                reg: row.reg,
                status: row.status,
                score: row.score,
                isLicensed: row.isLicensed,
              })),
            },
          },
        })
      );
    }

    if (intentId === "area_attendance") {
      const report = await fetchAreaAttendanceSummary();
      const lines =
        language === "en"
          ? `Area attendance **${report.startDate} → ${report.endDate}** (min. **${report.days}** day(s) present):\n\n- **${report.provinces}** province(s), **${report.departments}** department row(s)\n- **${report.teachersChecked}/${report.teachersAssigned}** teachers checked (**${report.checkRate}%**)`
          : `Synthèse présence zone **${report.startDate} → ${report.endDate}** (min. **${report.days}** jour(s)) :\n\n- **${report.provinces}** province(s), **${report.departments}** ligne(s) département\n- **${report.teachersChecked}/${report.teachersAssigned}** enseignantes vérifiées (**${report.checkRate} %**)`;

      return packData(
        msg({
          type: "stats",
          content: lines,
          cards: [
            { label: language === "en" ? "Checked" : "Vérifiées", value: report.teachersChecked },
            { label: language === "en" ? "Assigned" : "Affectées", value: report.teachersAssigned },
            { label: language === "en" ? "Rate" : "Taux", value: `${report.checkRate}%` },
          ],
          attendanceResults: report.preview,
          attendanceLabel: language === "en" ? "Sample departments" : "Exemples par département",
          actions: [
            {
              id: "nav-area-att",
              label: language === "en" ? "Open area summary" : "Ouvrir synthèse zone",
              route: "/synthese-presence-zone",
              variant: "primary",
            },
          ],
        }),
        buildRuleFacts({
          intent: "area_attendance",
          structuredText: lines.replace(/\*\*/g, ""),
          facts: { areaAttendance: report },
        })
      );
    }

    if (intentId === "license_summary") {
      const quota = await fetchLicenseQuota();
      const unlicensed = await fetchUnlicensedTeachers(5).catch(() => []);
      const lines =
        language === "en"
          ? `Teacher licenses:\n\n- **${quota.licensedCount}** / **${quota.maxLicensedTeachers || "—"}** licensed\n- **${quota.remainingLicenses}** slot(s) remaining\n- **${quota.unlicensedCount}** unlicensed of **${quota.totalTeachers}** registered`
          : `Licences enseignantes :\n\n- **${quota.licensedCount}** / **${quota.maxLicensedTeachers || "—"}** licencié(s)\n- **${quota.remainingLicenses}** créneau(x) restant(s)\n- **${quota.unlicensedCount}** non licencié(s) sur **${quota.totalTeachers}** inscrit(s)`;

      return packData(
        msg({
          type: "stats",
          content: lines,
          cards: [
            { label: language === "en" ? "Licensed" : "Licenciés", value: quota.licensedCount },
            { label: language === "en" ? "Remaining" : "Restants", value: quota.remainingLicenses, highlight: quota.remainingLicenses === 0 },
            { label: language === "en" ? "Unlicensed" : "Non licenciés", value: quota.unlicensedCount },
          ],
          teacherResults: unlicensed,
          actions: [
            {
              id: "nav-licenses",
              label: language === "en" ? "Open teacher licenses" : "Ouvrir licences",
              route: "/teacher-license",
              variant: "primary",
            },
          ],
        }),
        buildRuleFacts({
          intent: "license_summary",
          structuredText: lines.replace(/\*\*/g, ""),
          facts: { licenses: quota },
        })
      );
    }

    if (intentId === "establishment_attendance") {
      const report = await fetchEstablishmentAttendanceReport();
      const lines =
        language === "en"
          ? `Establishment attendance **${report.startDate} → ${report.endDate}** (radius **${report.radius}m**):\n\n- **${report.totalTeachers}** teacher(s) in report\n- **${report.withPresence}** with at least 1 day present\n- **${report.inRadius}** with presence inside radius`
          : `Rapport présence établissement **${report.startDate} → ${report.endDate}** (rayon **${report.radius} m**) :\n\n- **${report.totalTeachers}** enseignante(s)\n- **${report.withPresence}** avec au moins 1 jour de présence\n- **${report.inRadius}** dans le rayon`;

      return packData(
        msg({
          type: "stats",
          content: lines,
          cards: [
            { label: language === "en" ? "Teachers" : "Enseignantes", value: report.totalTeachers },
            { label: language === "en" ? "Present" : "Présentes", value: report.withPresence },
            { label: language === "en" ? "In radius" : "Dans rayon", value: report.inRadius },
          ],
          attendanceResults: report.lowAttendance?.length ? report.lowAttendance : report.preview,
          attendanceLabel: language === "en" ? "Lowest attendance (sample)" : "Présence la plus faible (échantillon)",
          actions: [
            {
              id: "nav-estab-att",
              label: language === "en" ? "Open attendance report" : "Ouvrir rapport présence",
              route: "/rapport-presence-etablissement",
              variant: "primary",
            },
          ],
        }),
        buildRuleFacts({
          intent: "establishment_attendance",
          structuredText: lines.replace(/\*\*/g, ""),
          facts: { establishmentAttendance: report },
        })
      );
    }

    if (intentId === "insights") {
      const insights = await loadProactiveInsights(context);
      if (!insights.length) {
        return msg({
          content:
            language === "en"
              ? "Everything looks good — no urgent items right now."
              : "Tout semble en ordre — aucun élément urgent pour le moment.",
        });
      }
      const insightText = insights.map((i) => i.title).join("; ");
      return packData(
        msg({
          type: "insights",
          content: language === "en" ? "Items that may need your attention:" : "Éléments à surveiller :",
          insights,
        }),
        buildRuleFacts({
          intent: "insights",
          structuredText: insightText || (language === "en" ? "No urgent items" : "Rien d'urgent"),
        })
      );
    }
  } catch (err) {
    console.error("[Assistant] data intent failed", intentId, err);
    return msg({
      type: "error",
      content:
        language === "en"
          ? "I couldn't fetch live data. Check your connection or try again."
          : "Impossible de récupérer les données. Vérifiez votre connexion ou réessayez.",
    });
  }

  return null;
}

function handleNavigation(input, context) {
  const { language, roleBucket, t, pathname } = context;
  if (shouldHandleWriteCommand(input)) return null;
  const routeKey = resolveRouteKey(input, language);
  if (!routeKey) return null;

  const routeDef = getRouteByKey(routeKey, roleBucket);
  if (!routeDef) {
    return msg({
      type: "error",
      content:
        language === "en"
          ? "That section requires admin permissions."
          : "Cette section nécessite des droits administrateur.",
    });
  }

  const aliases = NAV_ALIASES[language] || NAV_ALIASES.fr;
  const label = (aliases[routeKey] && aliases[routeKey][0]) || routeKey;

  return msg({
    content:
      language === "en"
        ? `Opening **${label}**…`
        : `Ouverture de **${label}**…`,
    actions: [{ id: `nav-${routeKey}`, label: t("assistant.action.open", "Ouvrir"), route: routeDef.route, variant: "primary", autoNavigate: true }],
  });
}

function handleCapabilities(context) {
  const { language, roleBucket, pathname, t } = context;
  const caps = getAssistantCapabilities(language, roleBucket);
  const pageHelp = getPageHelp(pathname, language);

  const intro =
    language === "en"
      ? "Here's what I can do for you in eVerifPro — pick a topic or type any example below:"
      : "Voici ce que je peux faire pour vous dans eVerifPro — choisissez un sujet ou tapez un exemple :";

  const lines = caps
    .map((c, i) => `**${i + 1}. ${c.title}** — ${c.description}\n   _Ex: « ${c.example} »_`)
    .join("\n\n");

  const pageNote = pageHelp
    ? language === "en"
      ? `\n\nYou're on **${pageHelp.title}** — say « help on this page » for screen-specific tips.`
      : `\n\nVous êtes sur **${pageHelp.title}** — dites « aide sur cette page » pour des conseils adaptés.`
    : "";

  return msg({
    type: "capabilities",
    content: `${intro}\n\n${lines}${pageNote}`,
    capabilityCards: caps.map((c) => ({
      id: c.id,
      title: c.title,
      description: c.description,
      icon: c.icon,
      message: c.message,
    })),
    actions: [
      {
        id: "cap-help-page",
        label: t("assistant.action.help_page", language === "en" ? "Help this page" : "Aide cette page"),
        action: "trigger:help_page",
        variant: "primary",
      },
      {
        id: "cap-summary",
        label: t("assistant.action.view_dashboard", language === "en" ? "Portal summary" : "Résumé portail"),
        action: "trigger:dashboard_summary",
      },
    ],
    suggestions: caps.slice(0, 4).map((c) => c.example),
  });
}

function handlePageHelp(context) {
  const { language, pathname, roleBucket, pageContext } = context;
  const help = getPageHelp(pathname, language);
  const pageAware = buildPageAwareContext(pathname, pageContext, language);

  if (!help) {
    const faq = searchFaq("aide", language, roleBucket);
    if (faq.length) {
      const f = faq[0];
      return msg({
        content: `**${f.question}**\n\n${f.answer}`,
        actions: f.route ? [{ id: "faq-nav", label: language === "en" ? "Open" : "Ouvrir", route: f.route, variant: "primary" }] : [],
      });
    }
    return msg({
      content:
        language === "en"
          ? "Ask about a specific page (e.g. « help assignments ») or browse FAQ topics."
          : "Demandez une page précise (ex. « aide affectations ») ou parcourez la FAQ.",
      suggestions: defaultSuggestions(language, roleBucket),
    });
  }

  const steps = help.steps.map((s, i) => `${i + 1}. ${s}`).join("\n");
  const screenBlock = pageAware?.summary
    ? language === "en"
      ? `\n\n**Your current filters:**\n${pageAware.summary}`
      : `\n\n**Vos filtres actuels :**\n${pageAware.summary}`
    : "";
  const tips =
    language === "en"
      ? "\n\n**Tip:** Ask cross-module questions like « Is teacher X assigned and attending? » or use the chips below."
      : "\n\n**Astuce :** Posez des questions croisées comme « Est-ce que X est affectée et présente ? » ou utilisez les puces ci-dessous.";

  return msg({
    type: "help",
    content: `**${help.title}**\n\n${help.summary}\n\n**${language === "en" ? "Steps" : "Étapes"}:**\n${steps}${screenBlock}${tips}`,
    pageAware,
    actions: [
      {
        id: "help-more",
        label: language === "en" ? "What else can you do?" : "Que peux-tu faire d'autre ?",
        action: "trigger:capabilities",
      },
    ],
    suggestions: pageAware?.suggestions?.length
      ? pageAware.suggestions
      : [
          language === "en" ? "Portal summary" : "Résumé du portail",
          language === "en" ? "Assignment guide" : "Guide affectation",
          language === "en" ? "Pending leave" : "Congés en attente",
        ],
  });
}

function handleFaqSearch(input, context) {
  if (shouldHandleWriteCommand(input)) return null;
  const { language, roleBucket } = context;
  const results = searchFaq(input, language, roleBucket);
  if (!results.length) return null;

  const top = results[0];
  const content =
    results.length === 1
      ? `**${top.question}**\n\n${top.answer}`
      : `**${top.question}**\n\n${top.answer}` +
        (results.length > 1
          ? `\n\n_${language === "en" ? "Related" : "Voir aussi"}: ${results.slice(1).map((r) => r.question).join(" · ")}_`
          : "");

  return {
    response: msg({
      type: "faq",
      content,
      actions: top.route
        ? [{ id: "faq-go", label: language === "en" ? "Go there" : "Y aller", route: top.route, variant: "primary" }]
        : [],
    }),
    ruleFacts: buildRuleFacts({
      intent: "faq",
      structuredText: `${top.question}: ${top.answer}`,
      faq: top,
    }),
  };
}

function buildOffTopicMessage(context) {
  const { language, roleBucket } = context;
  const en = language === "en";
  return msg({
    type: "text",
    content: en
      ? "I'm the **eVerifPro portal assistant** — I help with teachers, leave, attendance, licenses, assignments, and navigation inside this admin panel.\n\nI can't answer general web questions here. Try one of the suggestions below, or open **Response mode** in the header and choose **Cloud AI (Gemini)** or **Local AI** for broader questions."
      : "Je suis l'**assistant du portail eVerifPro** — enseignantes, congés, présence, licences, affectations et navigation dans ce panneau d'administration.\n\nJe ne peux pas répondre aux questions générales hors portail ici. Essayez une suggestion ci-dessous, ou ouvrez **Mode de réponse** dans l'en-tête et choisissez **IA cloud (Gemini)** ou **IA locale** pour des questions plus larges.",
    suggestions: [
      en ? "What can you do for me?" : "Que peux-tu faire pour moi ?",
      en ? "Portal summary" : "Résumé du portail",
      en ? "Help on this page" : "Aide sur cette page",
      ...defaultSuggestions(language, roleBucket).slice(0, 2),
    ],
  });
}

function buildUnknownFallback(context) {
  const { language, roleBucket } = context;
  const en = language === "en";
  const faqHint = en
    ? "Try rephrasing with portal keywords (teacher, leave, license, attendance, assignment), pick a chip below, or ask **What can you do?**"
    : "Reformulez avec des mots du portail (enseignante, congé, licence, présence, affectation), choisissez une puce ci-dessous, ou demandez **Que peux-tu faire ?**";
  const smartHint = isLlmEnabled()
    ? en
      ? "\n\nFor open-ended questions, click **Standard** / mode chip in the header and select **Cloud AI (Gemini)** or **Local AI**."
      : "\n\nPour les questions ouvertes, cliquez sur le mode dans l'en-tête et choisissez **IA cloud (Gemini)** ou **IA locale**."
    : "";

  return msg({
    content: en
      ? `I didn't match that to a portal action yet.\n\n${faqHint}${smartHint}`
      : `Je n'ai pas encore reconnu d'action portail correspondante.\n\n${faqHint}${smartHint}`,
    suggestions: [
      en ? "What can you do for me?" : "Que peux-tu faire pour moi ?",
      en ? "Help on this page" : "Aide sur cette page",
      en ? "Portal summary" : "Résumé du portail",
      en ? "Pending leave" : "Congés en attente",
      ...defaultSuggestions(language, roleBucket).slice(0, 2),
    ],
  });
}

async function deliverPageHelp(context, userMessage) {
  const response = handlePageHelp(context);
  const help = getPageHelp(context.pathname, context.language);
  const pageAware = buildPageAwareContext(context.pathname, context.pageContext, context.language);
  const structuredParts = [
    help ? `${help.title}: ${help.summary}. ${(help.steps || []).join(" ")}` : response.content,
    formatPageAwareForLlm(pageAware, context.language),
  ].filter(Boolean);

  return deliverHybrid(
    response,
    userMessage,
    context,
    buildRuleFacts({
      intent: "help_page",
      structuredText: structuredParts.join("\n"),
      pageHelp: help ? { title: help.title, summary: help.summary, steps: help.steps } : null,
      pageAware,
    })
  );
}

function startWizardMessage(workflowId, context) {
  const { language, roleBucket, t } = context;
  if (!roleCanWrite(roleBucket) && workflowId !== "attendance") {
    return msg({
      content:
        language === "en"
          ? "Your role has read-only access. I can show the guide but you'll need write permissions to complete actions."
          : "Votre rôle est en lecture seule. Je peux afficher le guide, mais les actions nécessitent des droits d'écriture.",
    });
  }

  const step = startWorkflow(workflowId, language);
  if (!step) return null;

  return msg({
    type: "wizard",
    content: language === "en" ? "Interactive guide — follow each step:" : "Guide interactif — suivez chaque étape :",
    wizard: {
      workflowId: step.workflowId,
      stepIndex: step.stepIndex,
      totalSteps: step.totalSteps,
      title: step.title,
      body: step.body,
      route: step.route,
    },
    actions: [
      { id: "wiz-next", label: t("assistant.action.next_step", "Étape suivante"), action: `wizard-next:${workflowId}:${step.stepIndex}` },
      { id: "wiz-open", label: t("assistant.action.open_page", "Ouvrir la page"), route: step.route },
    ],
  });
}

/** Answer short follow-ups from the last profile card — no API round-trip. */
function tryQuickTeacherFollowUpAnswer(query, context) {
  const { language, messages } = context;
  const profile = extractLastTeacherProfileFromMessages(messages);
  if (!profile?.teacher) return null;

  const q = String(query ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
  const en = language === "en";
  const name = profile.teacher.name || (en ? "This teacher" : "Cette enseignante");
  const assignment = profile.assignment;
  const leaveSummary = profile.leaveSummary || {};
  const teacher = profile.teacher;
  const suggestions = [
    en ? "Full profile" : "Fiche complète",
    en ? "Search another teacher" : "Chercher une autre enseignante",
  ];

  if (/\b(assigned|affect|affectee|affectée|affecte|affecté)\b/.test(q)) {
    if (assignment?.estabName) {
      const since = assignment.assignedDate ? ` (${en ? "since" : "depuis"} ${String(assignment.assignedDate).slice(0, 10)})` : "";
      return msg({
        type: "text",
        content: en
          ? `Yes — **${name}** is assigned to **${assignment.estabName}**${since}.`
          : `Oui — **${name}** est affectée à **${assignment.estabName}**${since}.`,
        suggestions,
        actions: [
          {
            id: "nav-profile",
            label: en ? "Full profile" : "Fiche complète",
            route: teacher.id ? `/enseignant/${teacher.id}` : "/enseignantes",
            variant: "primary",
          },
        ],
      });
    }
    return msg({
      type: "text",
      content: en
        ? `No — **${name}** is not currently assigned to an establishment.`
        : `Non — **${name}** n'est pas affectée à un établissement pour le moment.`,
      suggestions,
    });
  }

  if (/\b(on leave|on a leave|en conge|en congé|leave|conge|congé)\b/.test(q)) {
    const pending = leaveSummary.pending ?? leaveSummary.pendingCount ?? 0;
    const approved = leaveSummary.approved ?? leaveSummary.approvedCount ?? 0;
    if (pending > 0 || approved > 0 || leaveSummary.active) {
      const status = leaveSummary.activeStatus || (pending > 0 ? (en ? "pending" : "en attente") : en ? "approved" : "approuvé");
      return msg({
        type: "text",
        content: en
          ? `**${name}** has leave activity (${status}). Open the profile for dates and details.`
          : `**${name}** a des demandes de congé (${status}). Ouvrez la fiche pour les dates.`,
        suggestions,
        actions: teacher.id
          ? [{ id: "nav-profile", label: en ? "Full profile" : "Fiche complète", route: `/enseignant/${teacher.id}`, variant: "primary" }]
          : [],
      });
    }
    return msg({
      type: "text",
      content: en ? `**${name}** has no leave requests on record.` : `**${name}** n'a pas de demande de congé enregistrée.`,
      suggestions,
    });
  }

  if (/\b(license|licence|licensed|licencie|licencié)\b/.test(q)) {
    const licensed = teacher.isLicensed;
    return msg({
      type: "text",
      content: licensed
        ? en
          ? `Yes — **${name}** has an active license.`
          : `Oui — **${name}** a une licence active.`
        : en
          ? `No — **${name}** does not have an active license.`
          : `Non — **${name}** n'a pas de licence active.`,
      suggestions: [en ? "License quota" : "Quota licences", ...suggestions],
    });
  }

  return null;
}

async function executeAiRoute(route, trimmed, context) {
  const { language, roleBucket, lastTeacher } = context;
  const intentId = route.intent;

  if (intentId === "write_command") {
    const draft = writeDraftFromRoute(route);
    if (draft) {
      const msg = await executeClassifiedWriteDraft(draft, context);
      if (msg) return msg;
    }
    return null;
  }

  if (intentId === "teacher_story") {
    const teacherId = route.useLastTeacher ? lastTeacher?.id || null : null;
    const query =
      route.teacherQuery ||
      (route.useLastTeacher ? lastTeacher?.reg || lastTeacher?.name : null) ||
      null;
    if (teacherId || query) {
      const storyResult = await buildTeacherStoryResponse(context, { teacherId, query, userMessage: trimmed });
      return deliverDataResult(storyResult, trimmed, context);
    }
    return null;
  }

  if (intentId === "search_teacher" && route.teacherQuery) {
    const result = await handleDataIntent("search_teacher", route.teacherQuery, context);
    if (result?.response) return deliverDataResult(result, trimmed, context);
    if (result) return result;
    return null;
  }

  const dataIntents = [
    "count_teachers",
    "count_recruiters",
    "count_leaves",
    "count_assignments",
    "count_establishments",
    "dashboard_summary",
    "search_teacher",
    "area_attendance",
    "establishment_attendance",
    "license_summary",
    "insights",
  ];
  if (dataIntents.includes(intentId)) {
    const queryText = route.teacherQuery || trimmed;
    const result = await handleDataIntent(intentId, queryText, context);
    if (result?.response) return deliverDataResult(result, trimmed, context);
    if (result) return result;
    return null;
  }

  if (intentId === "capabilities") return handleCapabilities(context);
  if (intentId === "help_page") return deliverPageHelp(context, trimmed);
  if (intentId === "greeting") return buildWelcomeMessage(context);
  if (intentId === "thanks") {
    return msg({
      content: language === "en" ? "You're welcome! Anything else I can help with?" : "Avec plaisir ! Autre chose ?",
      suggestions: defaultSuggestions(language, roleBucket).slice(0, 3),
    });
  }
  if (intentId === "clear") {
    return { clear: true, message: msg({ content: language === "en" ? "Conversation cleared." : "Conversation effacée." }) };
  }
  if (intentId === "navigate") {
    const nav = handleNavigation(trimmed, context);
    if (nav) return nav;
  }

  if (intentId === "faq") {
    const faq = handleFaqSearch(trimmed, context);
    if (faq) return deliverDataResult(faq, trimmed, context);
  }

  return null;
}

export async function processMessage(input, context) {
  const trimmed = String(input ?? "").trim();
  const { language, roleBucket, wizardState, t, lastTeacher, aiRoutingEnabled } = context;

  if (!trimmed) {
    return msg({ content: language === "en" ? "Type a question or pick a suggestion below." : "Posez une question ou choisissez une suggestion." });
  }

  // Straightforward intents first — must run before teacher follow-up heuristics
  if (isCapabilitiesQuery(trimmed) || detectIntent(trimmed, language, roleBucket).id === "capabilities") {
    return handleCapabilities(context);
  }
  if (isHelpOnPageQuery(trimmed)) {
    return deliverPageHelp(context, trimmed);
  }

  if (isAwaitingLeaveDetails(context.messages)) {
    const leaveReply = await handleLeaveDetailsReply(trimmed, context);
    if (leaveReply) return leaveReply;
  }

  // Write commands before FAQ — "Assign X to Y" must preview/confirm, not return assignment FAQ
  if (shouldHandleWriteCommand(trimmed)) {
    const writeMsg = await handleWriteCommandMessage(trimmed, context);
    if (writeMsg) return writeMsg;
  }

  // FAQ before AI routing and teacher-search hijacks — "how to change password" must not become a teacher lookup
  if (isFaqQuery(trimmed, language, roleBucket)) {
    const faqEarly = handleFaqSearch(trimmed, context);
    if (faqEarly) return deliverDataResult(faqEarly, trimmed, context);
  }

  // Contextual teacher follow-up — reload last profile (leave, license, assignment…)
  if (isTeacherFollowUpQuery(trimmed)) {
    if (lastTeacher?.id || lastTeacher?.reg || lastTeacher?.name) {
      const quick = tryQuickTeacherFollowUpAnswer(trimmed, context);
      if (quick) return quick;

      const storyResult = await buildTeacherStoryResponse(context, {
        teacherId: lastTeacher.id || null,
        query: lastTeacher.reg || lastTeacher.name || null,
        userMessage: trimmed,
      });
      return deliverDataResult(storyResult, trimmed, context);
    }
    return msg({
      content:
        language === "en"
          ? "Which teacher do you mean? Look up a teacher first, then ask follow-ups like « is she on leave? »."
          : "De quelle enseignante parlez-vous ? Cherchez d'abord une fiche, puis posez une question de suivi.",
      suggestions: [
        language === "en" ? "Help me find a teacher" : "Aide-moi à trouver une enseignante",
        language === "en" ? "Portal summary" : "Résumé du portail",
      ],
    });
  }

  // Smart AI routing — optional; uses local model to pick intent then runs same handlers
  if (aiRoutingEnabled && isLlmEnabled()) {
    try {
      const routed = await classifyRouteIntent(trimmed, context);
      if (routed?.ok && isActionableRoute(routed.route)) {
        const aiResult = await executeAiRoute(routed.route, trimmed, context);
        if (aiResult) return aiResult;
      }
    } catch (err) {
      console.warn("[Assistant] AI route failed, falling back to rules", err?.message || err);
    }
  }

  let effectiveInput = trimmed;

  const awaitingTeacherName = isAwaitingTeacherSearchName(context.messages);
  const skipTeacherSearchReply =
    isFaqQuery(trimmed, language, roleBucket) ||
    isCapabilitiesQuery(trimmed) ||
    isHelpOnPageQuery(trimmed);
  const searchReplyTerm = skipTeacherSearchReply
    ? null
    : resolveTeacherSearchReply(effectiveInput, { awaitingName: awaitingTeacherName });
  if (searchReplyTerm) {
    const searchResult = await handleDataIntent("search_teacher", searchReplyTerm, context);
    if (searchResult?.response) return deliverDataResult(searchResult, trimmed, context);
    if (searchResult) return searchResult;
  }

  // Priority phrase checks (before generic intent scoring)
  if (isCapabilitiesQuery(effectiveInput) || detectIntent(effectiveInput, language, roleBucket).id === "capabilities") {
    return handleCapabilities(context);
  }

  if (isHelpOnPageQuery(effectiveInput)) {
    return deliverPageHelp(context, effectiveInput);
  }

  const intent = detectIntent(effectiveInput, language, roleBucket);

  if (intent.id === "clear") {
    return { clear: true, message: msg({ content: language === "en" ? "Conversation cleared." : "Conversation effacée." }) };
  }

  if (intent.id === "greeting") {
    return buildWelcomeMessage(context);
  }

  if (intent.id === "thanks") {
    return msg({
      content: language === "en" ? "You're welcome! Anything else I can help with?" : "Avec plaisir ! Autre chose ?",
      suggestions: [
        language === "en" ? "What can you do?" : "Que peux-tu faire ?",
        ...defaultSuggestions(language, roleBucket).slice(0, 3),
      ],
    });
  }

  if (intent.id === "capabilities") {
    return handleCapabilities(context);
  }

  if (intent.id === "faq") {
    const faq = handleFaqSearch(effectiveInput, context);
    if (faq) return deliverDataResult(faq, trimmed, context);
  }

  if (intent.id === "help_page") {
    const faq = handleFaqSearch(effectiveInput, context);
    if (faq) return deliverDataResult(faq, trimmed, context);
    const routeKey = resolveRouteKey(effectiveInput, language);
    if (routeKey) {
      const routeDef = getRouteByKey(routeKey, roleBucket);
      if (routeDef) {
        const help = getPageHelp(routeDef.route, language);
        if (help) {
          const steps = help.steps.map((s, i) => `${i + 1}. ${s}`).join("\n");
          const response = msg({
            type: "help",
            content: `**${help.title}**\n\n${help.summary}\n\n${steps}`,
            actions: [{ id: "help-nav", label: t("assistant.action.open", "Ouvrir"), route: routeDef.route, variant: "primary" }],
          });
          return deliverHybrid(
            response,
            trimmed,
            context,
            buildRuleFacts({
              intent: "help_page",
              structuredText: `${help.title}: ${help.summary}`,
              pageHelp: { title: help.title, summary: help.summary, steps: help.steps },
            })
          );
        }
      }
    }
    return deliverPageHelp(context, effectiveInput);
  }

  const wizardId = WIZARD_INTENT_MAP[intent.id];
  if (wizardId) {
    const wiz = startWizardMessage(wizardId, context);
    if (wiz) return { ...wiz, wizardState: { id: wizardId, stepIndex: 0 } };
  }

  const dataIntents = [
    "count_teachers",
    "count_recruiters",
    "count_leaves",
    "count_assignments",
    "count_establishments",
    "dashboard_summary",
    "teacher_story",
    "search_teacher",
    "area_attendance",
    "establishment_attendance",
    "license_summary",
    "insights",
  ];
  if (dataIntents.includes(intent.id)) {
    const result = await handleDataIntent(intent.id, effectiveInput, context);
    if (!result) return null;
    if (result.response) return deliverDataResult(result, trimmed, context);
    return result;
  }

  if (intent.id === "navigate" || resolveRouteKey(effectiveInput, language)) {
    const nav = handleNavigation(effectiveInput, context);
    if (nav) return nav;
  }

  const faq = handleFaqSearch(effectiveInput, context);
  if (faq) return deliverDataResult(faq, trimmed, context);

  // Fallback: try FAQ + navigation loosely
  const looseRoute = resolveRouteKey(effectiveInput, language);
  if (looseRoute) {
    const nav = handleNavigation(effectiveInput, context);
    if (nav) return nav;
  }

  // Off-topic — polite redirect without calling the LLM
  if (isOffTopicQuery(trimmed)) {
    return buildOffTopicMessage(context);
  }

  // Hybrid open-chat — only when user enabled Smart AI
  if (aiRoutingEnabled && isLlmEnabled()) {
    const prefetch = await prefetchLiveContext(effectiveInput, context, { broad: true });
    const ruleFacts = {
      intent: "open_chat",
      structuredText: prefetch?.liveData || "",
      pageHelp: prefetch?.pageHelp || null,
      facts: prefetch?.facts || null,
      pageAware: buildPageAwareContext(context.pathname, context.pageContext, context.language)?.summary
        ? buildPageAwareContext(context.pathname, context.pageContext, context.language)
        : null,
    };

    return {
      ...msg({
        type: "hybrid",
        content: "",
        suggestions: [
          language === "en" ? "Portal summary" : "Résumé du portail",
          language === "en" ? "Help on this page" : "Aide sur cette page",
          language === "en" ? "Pending leave" : "Congés en attente",
        ],
      }),
      llmPending: true,
      llmStreaming: true,
      enhanceRequest: {
        userMessage: trimmed,
        ruleFacts,
        intent: "open_chat",
        mode: "hybrid",
      },
    };
  }

  return buildUnknownFallback(context);
}

export async function processWizardNext(workflowId, stepIndex, context) {
  const { language, t } = context;
  const next = nextWorkflowStep(workflowId, stepIndex, language);

  if (next?.completed) {
    return {
      message: msg({
        type: "wizard",
        content:
          language === "en"
            ? "Guide complete! Open the page to finish."
            : "Guide terminé ! Ouvrez la page pour finaliser.",
        actions: [
          { id: "wiz-done", label: t("assistant.action.open_page", "Ouvrir la page"), route: next.route, variant: "primary", autoNavigate: true },
        ],
      }),
      wizardState: null,
    };
  }

  return {
    message: msg({
      type: "wizard",
      content: "",
      wizard: {
        workflowId: next.workflowId,
        stepIndex: next.stepIndex,
        totalSteps: next.totalSteps,
        title: next.title,
        body: next.body,
        route: next.route,
        altRoute: next.altRoute,
      },
      actions: [
        {
          id: "wiz-next",
          label: t("assistant.action.next_step", "Étape suivante"),
          action: `wizard-next:${next.workflowId}:${next.stepIndex}`,
        },
        { id: "wiz-open", label: t("assistant.action.open_page", "Ouvrir la page"), route: next.altRoute || next.route },
      ],
    }),
    wizardState: { id: workflowId, stepIndex: next.stepIndex },
  };
}

export async function processAction(action, context) {
  if (!action) return null;

  if (action.startsWith("wizard:")) {
    const id = action.replace("wizard:", "");
    const wiz = startWizardMessage(id, context);
    if (wiz) return { message: wiz, wizardState: { id, stepIndex: 0 } };
  }

  if (action.startsWith("wizard-next:")) {
    const [, wfId, idx] = action.split(":");
    return processWizardNext(wfId, Number(idx), context);
  }

  if (action === "refresh-insights") {
    const insights = await loadProactiveInsights(context);
    return {
      message: msg({
        type: "insights",
        content: context.language === "en" ? "Updated insights:" : "Alertes mises à jour :",
        insights,
      }),
    };
  }

  if (action === "trigger:help_page") {
    const q = context.language === "en" ? "help on this page" : "aide sur cette page";
    return { message: await deliverPageHelp(context, q) };
  }

  if (action === "trigger:dashboard_summary") {
    const result = await handleDataIntent("dashboard_summary", "portal summary", context);
    const message = result ? await deliverDataResult(result, "portal summary", context) : null;
    return message ? { message } : null;
  }

  if (action === "trigger:capabilities") {
    return { message: handleCapabilities(context) };
  }

  if (action.startsWith("teacher-story:")) {
    const teacherId = action.split(":")[1];
    if (!teacherId) return null;

    // If a write-command draft is waiting for a teacher pick, continue that flow.
    if (context.commandDraft?.command) {
      const message = await continueWriteCommand({ teacherId, commandDraft: context.commandDraft }, context);
      return message ? { message } : null;
    }

    const teacherRow = await findTeacherById(teacherId);
    const label = teacherRow?.name || `#${teacherId}`;
    const userMessage =
      context.language === "en" ? `Tell me about teacher ${label}` : `Fiche enseignant ${label}`;
    const result = await buildTeacherStoryResponse(context, {
      teacherId,
      query: "",
      userMessage,
    });
    if (!result) return null;
    if (result.response) {
      const message = await deliverDataResult(result, userMessage, context);
      return message ? { message } : null;
    }
    return { message: result };
  }

  if (action.startsWith("command-pick-establishment:")) {
    const establishmentId = action.split(":")[1];
    if (!establishmentId || !context.commandDraft) return null;
    const message = await continueWriteCommand(
      { establishmentId, commandDraft: context.commandDraft },
      context
    );
    return message ? { message } : null;
  }

  if (action.startsWith("command-pick-leave-type:")) {
    const leaveTypeId = action.split(":")[1];
    if (!leaveTypeId || !context.commandDraft) return null;
    const message = await continueWriteCommand(
      {
        leaveTypeId,
        leaveTypeName: context.leaveTypeName,
        commandDraft: context.commandDraft,
      },
      context
    );
    return message ? { message } : null;
  }

  if (action.startsWith("command-pick-leave:")) {
    const leaveRequestId = action.split(":")[1];
    if (!leaveRequestId || !context.commandDraft) return null;
    const message = await continueWriteCommand(
      { leaveRequestId, commandDraft: context.commandDraft },
      context
    );
    return message ? { message } : null;
  }

  if (action === "command-cancel") {
    return { message: cancelWriteCommandMessage(context.language) };
  }

  return null;
}

export async function executeAssistantWriteCommand(commandConfirm, context) {
  const message = await executeWriteCommand(commandConfirm, context);
  return message ? { message } : null;
}

export function getQuickPrompts(language, roleBucket) {
  return getQuickPromptTiles(language, roleBucket);
}
